import React, { useEffect, useMemo, useRef, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/South';
import KeyboardArrowUpIcon from '@mui/icons-material/North';
import { Box } from '@mui/material';

import ShareCollaborativeFolderModal from '../SharingModals/MyFolderPage/ShareFolderModal/CollaborativeFolderShare';
import { getCollaborativeFolders, getCollaborativeFoldersForParent } from '../../api/collaborativeFoldersApi';
import CollaborativeFolder from '../modules/Folders/CollaborativeFolder';
import { COLLABORATIVE_FOLDER_TYPE } from '../../enums';
import { PaginationFooter } from '../modules/PaginationFooter';
import { SortOptions, SortType } from '../../enums/SORT';
import styles from './CollaborativeFolders.module.scss';
import LinearProgress from '../modules/LinearProgress';
import { OrderSelector } from '../modules/Selector';
import { useAppSelector } from '../../store/hooks';
import SearchInput from '../modules/SearchInput';
import {
  getParentFoldersSortOptionAndType,
  getSortStateForParentFolders,
  setParentFoldersSortOptionAndType,
  useDimensions,
} from '../../helpers';
import DateSortHeader from '../modules/DateSortHeader';

const CollaborativeFolders = () => {
  const [folders, setFolders] = useState<COLLABORATIVE_FOLDER_TYPE[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [sort, setSort] = useState(SortType.ASC);
  const [sortField, setSortField] = useState(SortOptions.NAME);

  const { showShareModal, triggerFolderFetch } = useAppSelector((state) => state.folderStore);
  const { myOrganization } = useAppSelector((state) => state.user);

  const listRef = useRef<HTMLDivElement>(null);

  const { widthD } = useDimensions();

  const fetchData = async () => {
    try {
      setLoading(true);
      const res =
        myOrganization.type === 'parent'
          ? await getCollaborativeFoldersForParent({ page, limit, total, sort, sortField, search })
          : await getCollaborativeFolders({ page, limit, total, sort, sortField, search });
      if (res.data.documents) {
        setFolders(res.data.documents);
        setTotal(res.data.total);
      }
    } catch (err: any) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const [fetchTrigger, setFetchTrigger] = useState(0);
  useEffect(() => {
    if (myOrganization?.type) {
      fetchData();
    }
  }, [fetchTrigger, page, limit, sort, sortField, search, triggerFolderFetch, myOrganization]);

  useEffect(() => {
    const res = getParentFoldersSortOptionAndType();
    if (!res) return;
    const sortAndOption = res.split('/');
    if (sortAndOption[0] && sortAndOption[1]) {
      setSort(sortAndOption[0] as SortType);
      setSortField(sortAndOption[1] as SortOptions);
    }
  }, []);

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPage(0);
    setSearch(e.target.value);
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  };

  const sortHandler = (singleValue: SortOptions, option?: { label: string; value: string; order: SortType }) => {
    if (option) {
      setSort(option.order);
      setParentFoldersSortOptionAndType(option.order, option.value as SortOptions);
      setSortField(option.value as SortOptions);
    } else if (sortField === singleValue) {
      const sortT = sort === SortType.ASC ? SortType.DESC : SortType.ASC;
      setSort(sortT);
      setParentFoldersSortOptionAndType(sortT, singleValue);
      setSortField(singleValue);
    } else {
      const sortState = JSON.parse(getSortStateForParentFolders()!);
      let sortType = SortType.ASC;
      if (sortState && sortState[singleValue]) {
        sortType = sortState[singleValue];
      }
      setSort(sortType);
      setParentFoldersSortOptionAndType(sortType, singleValue);
      setSortField(singleValue);
    }
    setPage(0);
  };

  const onPaginate = (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
    setPage(newPage);
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  };

  const onRowsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setLimit(parseInt(e.target.value, 10));
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  };

  const { showSidebar } = useAppSelector((state) => state.sharedStore);
  const customWidth = useMemo(() => {
    if (widthD < 500) {
      return `calc(100vw - 80px)`;
    }
    return `calc(100vw - ${showSidebar ? 315 : 64}px - 64px)`;
  }, [showSidebar]);

  return (
    <div className={styles.myFolders}>
      <div className={styles.topBar}>
        <div className={styles.smallPath}>File Management</div>
        <div className={styles.pageTitle}> Parent Folders </div>
      </div>
      <div className={styles.searchWrapper}>
        <SearchInput quickFilter onChange={onSearch} className={styles.search} />
        <div className={styles.sortToolbar}>
          <OrderSelector
            title={'Sorting by'}
            selectedValue={sortField}
            selectedOrder={sort}
            options={[
              { label: 'A-Z ↓ ', value: SortOptions.NAME, order: SortType.ASC },
              { label: 'Z-A ↑ ', value: SortOptions.NAME, order: SortType.DESC },
              { label: 'Size ↓', value: SortOptions.SIZE, order: SortType.DESC },
              { label: 'Size ↑', value: SortOptions.SIZE, order: SortType.ASC },
              { label: 'Last Modified ↓', value: SortOptions.UPDATED_AT, order: SortType.DESC },
              { label: 'Last Modified ↑', value: SortOptions.UPDATED_AT, order: SortType.ASC },
              { label: 'Created At ↓', value: SortOptions.CREATED_AT, order: SortType.DESC },
              { label: 'Created At ↑', value: SortOptions.CREATED_AT, order: SortType.ASC },
            ]}
            onSelect={sortHandler}
          />
        </div>
      </div>
      {loading && (
        <>
          <Box sx={{ width: '100%' }}>
            <LinearProgress />
          </Box>
        </>
      )}
      {folders.length > 0 && (
        <>
          <div className={styles.toolbar}>
            <div className={styles.container}>
              <div className={styles.leftSide}>
                <div className={styles.name} onClick={() => sortHandler(SortOptions.NAME)}>
                  <button type="button" className={styles.btn}>
                    Name
                  </button>
                  {sortField === SortOptions.NAME && sort === SortType.ASC && (
                    <>
                      <KeyboardArrowDownIcon style={{ fontSize: '12px', marginLeft: '4px' }} />
                    </>
                  )}
                  {sortField === SortOptions.NAME && sort === SortType.DESC && (
                    <>
                      <KeyboardArrowUpIcon style={{ fontSize: '12px', marginLeft: '4px' }} />
                    </>
                  )}
                </div>
              </div>
              <div className={styles.rightSide}>
                <div className={styles.team}> Collaborators </div>
                <div className={styles.date}>
                  <DateSortHeader
                    sortOption={sortField}
                    sort={sort}
                    compact={widthD <= 1330}
                    onSelect={sortHandler}
                    arrowClassName={styles.arrow}
                  />
                </div>
                <div className={styles.size} onClick={() => sortHandler(SortOptions.SIZE)}>
                  {widthD > 1330 ? 'File Size' : 'Size'}
                  {sortField === SortOptions.SIZE && sort === SortType.DESC && (
                    <>
                      <KeyboardArrowDownIcon style={{ fontSize: '12px', marginLeft: '4px' }} />
                    </>
                  )}
                  {sortField === SortOptions.SIZE && sort === SortType.ASC && (
                    <>
                      <KeyboardArrowUpIcon style={{ fontSize: '12px', marginLeft: '4px' }} />
                    </>
                  )}
                </div>

                <div className={styles.duration}> {widthD > 1330 ? 'Upload Hours' : 'Hours'}</div>

                <div className={styles.actionBtns}>Actions</div>
              </div>
            </div>
          </div>
          <div ref={listRef} className={styles.foldersList} id={'folder_custom_scroller'}>
            {folders.map((folder, folderIndex) => (
              <div key={folder.id} className="mb-5" style={{ width: customWidth }}>
                <CollaborativeFolder
                  folderIndex={folderIndex}
                  folder={folder}
                  data={{ page, limit, search, sort, sortOption: sortField }}
                  fetchDataCallback={fetchData}
                />
              </div>
            ))}
          </div>
          <PaginationFooter
            hideVersion
            page={page}
            total={total}
            onPageChange={onPaginate}
            rowsPerPage={limit}
            onRowsPerPageChange={onRowsChange}
          />
        </>
      )}
      {/* {folders.length === 0 && status === STATUS.IDLE && (
        <div className={styles.noFoldersContainer}>
          <img className={styles.noFoldersIcon} src={NoFolderIcon} alt="no folders" />
          <div className={styles.noFoldersText}>You haven’t created any content yet.</div>
        </div>
      )} */}
      {showShareModal && <ShareCollaborativeFolderModal setFetchTrigger={setFetchTrigger} />}
    </div>
  );
};

export default CollaborativeFolders;
